import React from "react";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import SpeedIcon from "@mui/icons-material/Speed";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BuildIcon from "@mui/icons-material/Build";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQuery } from "@apollo/client";
import SubmitCustomerReviewMutation from "./SubmitCustomerReviewMutation.gql";
import GetCustomerReviewConfigQuery from "./GetCustomerReviewConfigQuery.gql";
import { toast } from "react-toastify";
import CompanyLogo from "../../components/Logo/CompanyLogo";

const MAX_STARS = 10;
const CATEGORIES = [
  "Délai de livraison trop long",
  "Produit abîmé ou cassé",
  "Produit incorrect ou incomplet",
  "Livreurs peu soigneux",
  "Livreurs peu aimables",
  "Difficulté à joindre le service client",
  "Créneau horaire non respecté",
  "Manque d'informations sur le suivi",
  "Problème à la reprise de l'ancien appareil",
  "Autre",
];

const validationSchema = yup.object({
  star_rating: yup
    .number()
    .min(1, "Veuillez donner une note")
    .max(MAX_STARS)
    .required("Veuillez donner une note"),
  star_rating_comment: yup.string().max(1000, "1000 caractères max."),
});

const CustomerReviews: React.FC = () => {
  // Driver compliment icons - all colors unique
  const DRIVER_COMPLIMENTS = [
    {
      key: "ponctuel",
      label: "Ponctuel, créneau respecté",
      icon: <QueryBuilderIcon fontSize="large" sx={{ color: "#1976d2" }} />, // blue
      emoji: "🕒",
    },
    {
      key: "rapide",
      label: "Rapide et efficace",
      icon: <SpeedIcon fontSize="large" sx={{ color: "#43a047" }} />, // green
      emoji: "🚀",
    },
    {
      key: "sympa",
      label: "Sympathique et courtois",
      icon: (
        <SentimentVerySatisfiedIcon
          fontSize="large"
          sx={{ color: "#e91e63" }}
        />
      ), // pink
      emoji: "😊",
    },
    {
      key: "precaution",
      label: "Précautionneux et soigneux",
      icon: <MoveToInboxIcon fontSize="large" sx={{ color: "#8e24aa" }} />, // purple
      emoji: "📦",
    },
    {
      key: "pro",
      label: "Professionnel et compétent",
      icon: <VerifiedUserIcon fontSize="large" sx={{ color: "#0097a7" }} />, // teal
      emoji: "✅",
    },
    {
      key: "installation",
      label: "Installation impeccable",
      icon: <BuildIcon fontSize="large" sx={{ color: "#ff9800" }} />, // orange
      emoji: "🔧",
    },
  ];
  const [selectedCompliments, setSelectedCompliments] = React.useState<
    string[]
  >([]);

  const handleComplimentClick = (key: string) => {
    setSelectedCompliments((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  // Initial config query
  const {
    data,
    loading: configLoading,
    error: configError,
  } = useQuery(GetCustomerReviewConfigQuery);
  
  // URL Google Review récupérée depuis la configuration ou fallback
  const googleReviewUrl = data?.customerReviewConfig?.googleReviewUrl || "https://g.page/r/CS0keLnG8WybEB0/review";
  // Configuration isEthical pour déterminer si on affiche le modal Google Review pour toutes les notes
  const isEthical = data?.customerReviewConfig?.isEthical || false;
  const [hover, setHover] = React.useState<number | null>(null);
  const [googleModalOpen, setGoogleModalOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    null
  );
  const [copied, setCopied] = React.useState(false);
  const [pendingValues, setPendingValues] = React.useState<any>(null);
  const [thankYou, setThankYou] = React.useState(false);

  // Track which modal to show after thank you
  const [pendingModal, setPendingModal] = React.useState<null | "google">(null);

  const [submitCustomerReview, { loading }] = useMutation(
    SubmitCustomerReviewMutation,
    {
      onCompleted: (data) => {
        if (data?.submitCustomerReview?.success) {
          setThankYou(true);
          // The toast is good, but we can rely on the thank you page for confirmation.
          // toast.success("Merci pour votre avis !");
        } else {
          toast.error(
            data?.submitCustomerReview?.errorMessage || "Erreur lors de l'envoi"
          );
        }
      },
      onError: () => toast.error("Une erreur technique est survenue."),
    }
  );

  const formik = useFormik({
    initialValues: {
      star_rating: 0,
      star_rating_comment: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // Submit immediately
      submitCustomerReview({
        variables: {
          input: {
            ...values,
            ...(values.star_rating <= 6 && selectedCategory
              ? { star_rating_category: selectedCategory }
              : {}),
            ...(values.star_rating > 6
              ? { star_rating_category: undefined }
              : {}),
            driver_compliments: JSON.stringify(selectedCompliments),
          },
        },
      });
      // Show Google modal for > 6 stars OR if isEthical is true (all ratings)
      if (values.star_rating > 6 || isEthical) {
        setPendingValues(values);
        setPendingModal("google");
      } else {
        // For lower ratings (when not ethical), we just show the thank you page without a modal.
        setThankYou(true);
      }
    },
  });

  // Show modal after thank you page is shown
  React.useEffect(() => {
    if (thankYou && pendingModal) {
      setTimeout(() => {
        if (pendingModal === "google") setGoogleModalOpen(true);
        setPendingModal(null);
      }, 300); // slight delay to ensure thank you page renders first
    }
  }, [thankYou, pendingModal]);

  // For > 6 stars, open modal for Google, then submit on Terminer
  const handleGoogleModalClose = () => {
    setGoogleModalOpen(false);
    setCopied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        pendingValues?.star_rating_comment || ""
      );
      setCopied(true);
      toast.success("Commentaire copié dans le presse-papiers !");
    } catch {
      toast.error("Erreur lors de la copie");
    }
  };

  if (configLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 200px)", // Make it viewport height aware
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  // Show error message from API if present
  if (data?.customerReviewConfig?.errorMessage) {
    return (
      <Box
        sx={{
          textAlign: "center",
          color: "text.secondary",
          p: 4,
          mt: { xs: 2, sm: 8 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Oops, lien invalide
        </Typography>
        <Typography>
          La commande est introuvable. Merci de vérifier que le lien dans votre
          email ou SMS est correct.
        </Typography>
      </Box>
    );
  }
  if (data?.customerReviewConfig?.isReviewed) {
    return (
      <Box
        sx={{
          maxWidth: 500,
          mx: "auto",
          mt: { xs: 0, sm: 4 },
          px: { xs: 3, sm: 4 },
          py: { xs: 4, sm: 5 },
          background: "linear-gradient(145deg, #2E3A59 0%, #1B243B 100%)",
          borderRadius: { xs: 0, sm: 5 },
          boxShadow: { xs: 0, sm: "0 25px 50px -12px rgba(0,0,0,0.25)" },
          textAlign: "center",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-block",
            fontSize: { xs: 56, sm: 64 },
            mb: 2,
            animation: "float 3s ease-in-out infinite",
            "@keyframes float": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-10px)" },
            },
          }}
        >
          🎉
        </Box>
        <Typography
          variant="h4"
          gutterBottom
          fontWeight={700}
          sx={{
            color: "white",
            mb: 1,
            textShadow: "0 2px 5px rgba(0,0,0,0.3)",
            fontSize: { xs: "1.75rem", sm: "2.125rem" },
          }}
        >
          Avis déjà partagé !
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "rgba(255,255,255,0.8)",
            fontSize: { xs: "1rem", sm: "1.1rem" },
            fontWeight: 400,
            lineHeight: 1.6,
          }}
        >
          Merci, nous avons déjà bien reçu votre retour pour cette commande.
        </Typography>
      </Box>
    );
  }

  if (thankYou) {
    return (
      <>
        <Box
          sx={{
            maxWidth: 500,
            mx: "auto",
            mt: { xs: 0, sm: 4 },
            px: { xs: 3, sm: 4 },
            py: { xs: 4, sm: 5 },
            background: "linear-gradient(145deg, #2E3A59 0%, #1B243B 100%)",
            borderRadius: { xs: 0, sm: 5 },
            boxShadow: { xs: 0, sm: "0 25px 50px -12px rgba(0,0,0,0.25)" },
            textAlign: "center",
          }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              fontSize: { xs: 56, sm: 64 },
              mb: 2,
              animation: "float 3s ease-in-out infinite",
              "@keyframes float": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(-10px)" },
              },
              color: "#FFD700", // gold accent for the truck
            }}
          >
            <LocalShippingIcon sx={{ fontSize: { xs: 56, sm: 64 } }} />
          </Box>
          <Typography
            variant="h4"
            gutterBottom
            fontWeight={700}
            sx={{
              color: "white",
              mb: 1,
              textShadow: "0 2px 5px rgba(0,0,0,0.3)",
              fontSize: { xs: "1.75rem", sm: "2.125rem" },
            }}
          >
            Merci pour votre avis !
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.8)",
              mb: 2,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Grâce à vous, nous pouvons continuer à nous améliorer.
          </Typography>
        </Box>
        {/* Google Modal for > 6 stars */}
        <Dialog
          open={googleModalOpen}
          onClose={handleGoogleModalClose}
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: 420,
              margin: 2, // Use margin for spacing on mobile
              background:
                "linear-gradient(145deg, hsl(225, 25%, 30%) 0%, hsl(225, 25%, 15%) 100%)",
              borderRadius: 4,
              boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              color: "white",
              fontWeight: 700,
              fontSize: { xs: 20, sm: 22 },
              pt: 3,
              px: 3,
            }}
          >
            Souhaitez-vous partager votre avis sur Google ?
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 3 }}>
            <Box sx={{ textAlign: "center", mb: 2.5 }}>
              <Typography
                sx={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}
              >
                Aidez d&apos;autres personnes en le partageant sur Google.
              </Typography>
            </Box>
            {pendingValues?.star_rating_comment &&
              pendingValues.star_rating_comment.trim() !== "" && (
                <Box sx={{ width: "100%", textAlign: "center", mb: 2 }}>
                  <TextField
                    value={pendingValues.star_rating_comment}
                    label="Votre commentaire (prêt à copier)"
                    multiline
                    minRows={2}
                    maxRows={6}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(0,0,0,0.2)",
                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.2)",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7) !important",
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                        fontSize: "0.95rem",
                      },
                    }}
                  />
                  <Button
                    onClick={handleCopy}
                    variant="outlined"
                    color={copied ? "success" : "primary"}
                    size="small"
                    fullWidth
                    sx={{
                      fontWeight: 500,
                      borderRadius: 2,
                      color: copied ? "#66bb6a" : "white",
                      borderColor: copied ? "#66bb6a" : "rgba(255,255,255,0.3)",
                      background: "rgba(255,255,255,0.08)",
                      textTransform: "none",
                      "&:hover": {
                        background: "rgba(255,255,255,0.16)",
                        borderColor: copied ? "#66bb6a" : "#FFD700",
                      },
                    }}
                  >
                    {copied ? "Copié !" : "Copier mon commentaire"}
                  </Button>
                </Box>
              )}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => {
                window.open(googleReviewUrl, "_blank", "noopener,noreferrer");
                handleGoogleModalClose();
              }}
              sx={{
                mt: pendingValues?.star_rating_comment ? 1 : 2,
                fontWeight: 600,
                fontSize: 16,
                py: 1.25,
                borderRadius: 2,
                background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                color: "#212121",
                boxShadow: "0 8px 25px rgba(255, 215, 0, 0.25)",
                textTransform: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #ffdf33 0%, #ffb133 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 35px rgba(255, 215, 0, 0.35)",
                },
              }}
            >
              Laisser un avis Google
            </Button>
            <Button
              onClick={handleGoogleModalClose}
              fullWidth
              sx={{
                mt: 1.5,
                color: "rgba(255,255,255,0.6)",
                textTransform: "none",
                fontSize: 14,
                "&:hover": { background: "rgba(255,255,255,0.05)" },
              }}
            >
              Non merci
            </Button>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Box
      sx={{
        minHeight: { xs: "100vh", sm: "auto" }, // Only fill viewport on mobile
        maxWidth: 500,
        width: "100%",
        mx: "auto",
        mt: { xs: 0, sm: 4 },
        background: "linear-gradient(145deg, #2E3A59 0%, #1B243B 100%)",
        borderRadius: { xs: 0, sm: 5 },
        boxShadow: { xs: 0, sm: "0 25px 50px -12px rgba(0,0,0,0.25)" },
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: { xs: "space-between", sm: "initial" }, // Only push logo to bottom on mobile
      }}
      className="customer-reviews"
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 3, sm: 4 },
          py: { xs: 4, sm: 5 },
          textAlign: "center",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          fontWeight={700}
          sx={{
            color: "white",
            mb: 1,
            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            fontSize: { xs: "1.75rem", sm: "2.125rem" },
          }}
        >
          ⭐ Votre avis compte !
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: "rgba(255,255,255,0.8)",
            mb: 4,
            fontSize: { xs: "0.95rem", sm: "1.05rem" },
            lineHeight: 1.6,
          }}
        >
          Partagez votre expérience et aidez-nous à nous améliorer.
        </Typography>
        {/* Stars first */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 2, sm: 3 },
            background: "rgba(255,255,255,0.05)",
            borderRadius: 3,
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "white",
              mb: 2,
              fontWeight: 600,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
            }}
          >
            Comment s&apos;est passée votre livraison ?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: { xs: 1, sm: 1.5 },
              mb: 2,
            }}
          >
            {[...Array(MAX_STARS)].map((_, i) => {
              const starValue = i + 1;
              return (
                <StarIcon
                  key={starValue}
                  sx={{
                    fontSize: { xs: 22, sm: 26 },
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    color:
                      (hover ?? formik.values.star_rating ?? 0) >= starValue
                        ? "#FFD700"
                        : "rgba(255,255,255,0.3)",
                    filter:
                      (hover ?? formik.values.star_rating ?? 0) >= starValue
                        ? "drop-shadow(0 0 6px rgba(255,215,0,0.7))"
                        : "none",
                    transform:
                      (hover ?? formik.values.star_rating ?? 0) >= starValue
                        ? "scale(1.15)"
                        : "scale(1)",
                    "&:hover": {
                      transform: "scale(1.2)",
                      filter: "drop-shadow(0 0 10px rgba(255,215,0,0.9))",
                    },
                  }}
                  onClick={() => {
                    formik.setFieldValue("star_rating", starValue);
                    if (starValue > 6 && selectedCategory) {
                      setSelectedCategory("");
                    }
                  }}
                  onMouseEnter={() => setHover(starValue)}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`star-${starValue}`}
                />
              );
            })}
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.8)",
              fontWeight: 500,
              fontSize: "0.95rem",
              minHeight: "1.4em", // prevent layout shift
            }}
          >
            {formik.values.star_rating
              ? `${formik.values.star_rating} sur ${MAX_STARS} étoiles`
              : "Cliquez sur les étoiles pour noter"}
          </Typography>
        </Box>
        {/* Driver compliments after stars */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              color: "white",
              mb: 2,
              fontWeight: 600,
              fontSize: { xs: "1.1rem", sm: "1.2rem" },
              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
            }}
          >
            Un compliment pour vos livreurs ?
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
              gap: 1.5,
              maxWidth: "min(100%, calc(3 * (100px + 12px)))", // More flexible calculation
              mx: "auto",
            }}
          >
            {DRIVER_COMPLIMENTS.map((compliment) => (
              <Button
                key={compliment.key}
                onClick={() => handleComplimentClick(compliment.key)}
                variant="contained"
                sx={{
                  height: 90,
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 3,
                  background: selectedCompliments.includes(compliment.key)
                    ? "linear-gradient(145deg, #FFD700, #FFA500)"
                    : "rgba(255,255,255,0.05)",
                  border: selectedCompliments.includes(compliment.key)
                    ? "1px solid #FFD700"
                    : "1px solid rgba(255,255,255,0.1)",
                  boxShadow: selectedCompliments.includes(compliment.key)
                    ? "0 6px 12px rgba(255, 215, 0, 0.2)"
                    : "none",
                  color: selectedCompliments.includes(compliment.key)
                    ? "#1B243B"
                    : "white",
                  py: 1,
                  px: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: selectedCompliments.includes(compliment.key)
                      ? "0 8px 16px rgba(255, 215, 0, 0.3)"
                      : "0 4px 8px rgba(0,0,0,0.1)",
                    background: selectedCompliments.includes(compliment.key)
                      ? "linear-gradient(145deg, #FFD700, #FFA500)"
                      : "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Box sx={{ mb: 0.5 }}>
                  {React.cloneElement(compliment.icon, {
                    fontSize: "medium",
                    sx: {
                      ...compliment.icon.props.sx,
                      color: selectedCompliments.includes(compliment.key)
                        ? "#1B243B"
                        : compliment.icon.props.sx.color,
                    },
                  })}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    fontWeight: selectedCompliments.includes(compliment.key)
                      ? 700
                      : 500,
                    textAlign: "center",
                    lineHeight: 1.3,
                    color: "inherit",
                  }}
                >
                  {compliment.label}
                </Typography>
              </Button>
            ))}
          </Box>
        </Box>
        <form onSubmit={formik.handleSubmit}>
          {formik.values.star_rating > 0 && formik.values.star_rating <= 6 && (
            <>
              <TextField
                select
                fullWidth
                required
                label="Dites-nous ce qui vous a déplu"
                value={selectedCategory ?? ""}
                onChange={(e) => setSelectedCategory(e.target.value)}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    background: "rgba(255,255,255,0.05)",
                    borderRadius: 3,
                    "& fieldset": {
                      borderColor: "rgba(255,255,255,0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#FFD700",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.7) !important",
                  },
                  "& .MuiInputLabel-shrink": {
                    color: "white !important",
                  },
                  "& .Mui-focused.MuiInputLabel-root": {
                    color: "white !important",
                  },
                  "& .MuiSelect-select": {
                    color: "white",
                  },
                  "& .MuiSelect-icon": {
                    color: "rgba(255,255,255,0.7)",
                  },
                }}
                error={formik.values.star_rating <= 6 && !selectedCategory}
                helperText={
                  formik.values.star_rating <= 6 && !selectedCategory
                    ? "Ce champ est requis pour les notes de 6 étoiles ou moins."
                    : ""
                }
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat} sx={{ fontSize: 14 }}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
          {formik.touched.star_rating && formik.errors.star_rating && (
            <Typography
              color="error.light"
              variant="caption"
              display="block"
              textAlign="center"
              mb={1.5}
            >
              {formik.errors.star_rating}
            </Typography>
          )}
          <TextField
            label="Ajoutez un commentaire (facultatif)"
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            name="star_rating_comment"
            value={formik.values.star_rating_comment}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            variant="outlined"
            sx={{
              mb: 4,
              "& .MuiOutlinedInput-root": {
                background: "rgba(255,255,255,0.05)",
                borderRadius: 3,
                "& fieldset": {
                  borderColor: "rgba(255,255,255,0.2)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255,255,255,0.4)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FFD700",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "rgba(255,255,255,0.7) !important",
              },
              "& .MuiInputLabel-shrink": {
                color: "white !important",
              },
              "& .Mui-focused.MuiInputLabel-root": {
                color: "white !important",
              },
              "& .MuiInputBase-input": {
                color: "white",
              },
            }}
            error={
              formik.touched.star_rating_comment &&
              Boolean(formik.errors.star_rating_comment)
            }
            helperText={
              formik.touched.star_rating_comment &&
              formik.errors.star_rating_comment
            }
          />
          <Box sx={{ textAlign: "center" }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={
                loading ||
                !formik.isValid ||
                (formik.values.star_rating <= 6 && !selectedCategory)
              }
              sx={{
                borderRadius: 50, // Pill shape
                px: 5,
                py: 1.5,
                fontWeight: 700,
                fontSize: "1.05rem",
                background: "linear-gradient(145deg, #FFD700, #FFA500)",
                color: "#1B243B",
                boxShadow: "0 8px 16px rgba(255, 215, 0, 0.25)",
                textTransform: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(145deg, #FFC700, #FF9500)",
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 24px rgba(255, 215, 0, 0.35)",
                },
                "&:disabled": {
                  background: "rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.4)",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#1B243B" }} />
              ) : (
                "Envoyer mon avis"
              )}
            </Button>
          </Box>
        </form>
      </Box>
      {/* Company logo at the bottom */}
      <Box
        sx={{
          mt: { xs: "auto", sm: 6 },
          mb: { xs: 0, sm: 2 },
          pt: { xs: 4, sm: 0 },
          pb: { xs: 2, sm: 0 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          background: "transparent", // Ensure seamless blend
        }}
      >
        <CompanyLogo
          logoUrl={data?.customerReviewConfig?.companyLogo ?? "no-logo"}
        />
      </Box>
    </Box>
  );
};

export default CustomerReviews;
